import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { HelpCircle, ExternalLink } from 'lucide-react';

const CredentialHelper = ({ field }) => {
  const helpContent = {
    waba_id: {
      title: 'WhatsApp Business Account ID',
      description: 'A unique identifier for your WhatsApp Business Account. Found in Meta Business Manager under WhatsApp Business Accounts settings.',
      steps: [
        'Go to Meta Business Suite → Settings',
        'Click on "WhatsApp Business Accounts"',
        'Your WABA ID is displayed under account details'
      ],
      link: 'https://developers.facebook.com/docs/whatsapp/cloud-api/get-started',
      linkText: 'WhatsApp Cloud API Setup Guide'
    },
    phone_number_id: {
      title: 'Phone Number ID',
      description: 'The unique identifier for your registered WhatsApp phone number. Different from the phone number itself.',
      steps: [
        'In Meta Developer Dashboard, go to WhatsApp → API Setup',
        'Find "Phone number ID" under the phone number section',
        'Copy the numeric ID (not the phone number)'
      ],
      link: 'https://developers.facebook.com/docs/whatsapp/cloud-api/reference/phone-numbers',
      linkText: 'Phone Numbers API Reference'
    },
    access_token: {
      title: 'Permanent Access Token',
      description: 'A system user token that allows API access. Create a System User with WhatsApp permissions for a permanent token.',
      steps: [
        'Go to Meta Business Settings → Users → System Users',
        'Create a new System User with Admin role',
        'Add "WhatsApp Business Management" permission',
        'Generate and copy the access token'
      ],
      link: 'https://developers.facebook.com/docs/whatsapp/business-management-api/get-started#system-user-access-tokens',
      linkText: 'System User Access Tokens Guide'
    },
    phone_number: {
      title: 'WhatsApp Phone Number',
      description: 'The phone number registered with WhatsApp Business. Must include country code.',
      steps: [
        'Use format: +[country code][number]',
        'Example: +14155551234',
        'Must be a number not already on WhatsApp'
      ],
      link: 'https://developers.facebook.com/docs/whatsapp/cloud-api/get-started#phone-number',
      linkText: 'Phone Number Requirements'
    },
    business_name: {
      title: 'Business Display Name',
      description: 'The name shown to customers in WhatsApp. Must match your registered business name and follow Meta guidelines.',
      steps: [
        'Use your official registered business name',
        'Avoid generic names or trademarks you don\'t own',
        'Maximum 512 characters'
      ],
      link: 'https://developers.facebook.com/docs/whatsapp/guides/display-name',
      linkText: 'Display Name Guidelines'
    },
    webhook_url: {
      title: 'Webhook Forward URL',
      description: 'Optional URL where incoming messages will be forwarded. Your server must accept POST requests with WhatsApp webhook format.',
      steps: [
        'Set up an HTTPS endpoint on your server',
        'Endpoint must respond with 200 OK',
        'Messages are forwarded in WhatsApp format'
      ],
      link: 'https://developers.facebook.com/docs/whatsapp/cloud-api/webhooks/components',
      linkText: 'Webhook Components Reference'
    }
  };

  const help = helpContent[field];
  if (!help) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="ml-1 text-[#8A8A8B] hover:text-[#595C5C] transition-colors">
            <HelpCircle className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-sm p-4 bg-white border border-[#D9E2E5] shadow-lg">
          <div className="space-y-3">
            <h4 className="font-semibold text-[#131517] text-[14px]">{help.title}</h4>
            <p className="text-[12px] text-[#595C5C] leading-relaxed">{help.description}</p>
            
            <div className="space-y-1">
              <p className="text-[11px] font-semibold text-[#8A8A8B] uppercase">How to get it:</p>
              <ol className="text-[11px] text-[#595C5C] space-y-1 list-decimal list-inside">
                {help.steps.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
            </div>
            
            <a 
              href={help.link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[12px] text-[#00A884] hover:underline"
            >
              <ExternalLink className="w-3 h-3" />
              {help.linkText}
            </a>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Setup Guide Card Component
export const WhatsAppSetupGuide = () => {
  return (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-start gap-3">
        <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="space-y-2">
          <h4 className="font-semibold text-blue-900 text-[14px]">New to WhatsApp Business API?</h4>
          <p className="text-[12px] text-blue-700 leading-relaxed">
            To use the WhatsApp Cloud API, you'll need to set up a Meta Business account and create a WhatsApp Business Account (WABA).
          </p>
          <div className="flex flex-wrap gap-3 pt-1">
            <a 
              href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[12px] text-blue-700 hover:text-blue-900 font-medium"
            >
              <ExternalLink className="w-3 h-3" />
              Quick Start Guide
            </a>
            <a 
              href="https://business.facebook.com/settings/whatsapp-business-accounts" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[12px] text-blue-700 hover:text-blue-900 font-medium"
            >
              <ExternalLink className="w-3 h-3" />
              Meta Business Settings
            </a>
            <a 
              href="https://developers.facebook.com/apps" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[12px] text-blue-700 hover:text-blue-900 font-medium"
            >
              <ExternalLink className="w-3 h-3" />
              Meta Developer Portal
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CredentialHelper;
